import { promoCodesApi } from 'api/promoCodes'
import { MainTitle } from 'components/ui'
import { MobileFilterTitle } from 'components/ui/MobileFilterTitle/MobileFilterTitle'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { EDiscountType, EPromoStatus, IPromoCode } from 'models/promoCodes'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { cleanObject } from 'shared/utils/cleanObject'
import { showServerError } from 'shared/utils/showServerError'
import { useNavigate, useSearchParams } from 'react-router-dom'
import qs from 'qs'
import Table, { ColumnsType } from 'antd/es/table'
import {
  Button,
  Checkbox,
  Input,
  InputRef,
  Popconfirm,
  Radio,
  Space,
  Tag,
  Typography,
  message
} from 'antd'
import moment from 'moment'
import { promoCodesStatuses } from 'shared/constants/promoCodes'
import {
  FilterFilled,
  InboxOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  PlusOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { ERoles } from 'models/auth'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { appSelectors } from 'store/app'
import { adminRoutes, superAdminRoutes } from 'shared/constants/routes'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import styles from './PromoCodesList.module.scss'

interface IPromoCodesListProps {
  sellerId?: string
}

export const PromoCodesList: React.FC<IPromoCodesListProps> = ({
  sellerId
}) => {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()

  const role = useAppSelector(appSelectors.selectRole)
  const userSellerId = useAppSelector(appSelectors.selectSellerId)

  const [promoCodes, setPromoCodes] = useState<IPromoCode[]>([])
  const [isFirstRequestFinished, setIsFirstRequestFinished] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [isExportingStatistics, setIsExportingStatistics] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 100

  const [codeSearch, setCodeSearch] = useState('')
  const codeSearchRef = useRef<InputRef>(null)

  const [status, setStatus] = useState<EPromoStatus | undefined>(undefined)
  const [showArchived, setShowArchived] = useState(false)

  const isSuperAdmin = useMemo(() => role === ERoles.ADMIN, [role])

  const getPromoCodes = async (
    skip: number,
    status: EPromoStatus | undefined,
    code?: string
  ) => {
    try {
      setIsLoading(true)
      const response = await promoCodesApi.getPromoCodes({
        skip: String(skip),
        limit: String(pageSize),
        seller_id:
          sellerId ?? (isSuperAdmin ? undefined : userSellerId ?? undefined),
        code: code ?? codeSearch ?? undefined,
        status
      })
      setTotal(response.data.count)
      setPromoCodes(response.data.items)
      setSearchParams(
        cleanObject({
          page: skip / pageSize + 1 === 1 ? '' : String(skip / pageSize + 1),
          status: status ?? '',
          code: code ?? codeSearch
        })
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (window.location.search) {
      const params = qs.parse(window.location.search.substring(1))
      if (params.page) {
        setCurrentPage(Number(params.page))
      }
      if (params.status) {
        setStatus(params.status as EPromoStatus)
      }
      if (params.code) {
        setCodeSearch(params.code as string)
      }
    }
    setIsFirstRequestFinished(true)
  }, [])

  useEffect(() => {
    if (isFirstRequestFinished) {
      getPromoCodes((currentPage - 1) * pageSize, status)
      setCurrentPage(currentPage)
    }
  }, [isFirstRequestFinished])

  const onPageChange = (current: number) => {
    getPromoCodes((current - 1) * pageSize, status, codeSearch)
    setCurrentPage(current)
  }

  const saveFieldValue = (code: string) => {
    getPromoCodes(0, status, code)
    setCurrentPage(1)
  }

  const onChangeStatus = (newStatus: EPromoStatus | undefined) => {
    getPromoCodes(0, newStatus)
    setCurrentPage(1)
  }

  const archivePromoCode = async (id: string) => {
    try {
      await promoCodesApi.changePromoCodeStatus(id, {
        status: EPromoStatus.ARCHIVED
      })
      setPromoCodes((prev) =>
        prev.map((el) =>
          el._id === id ? { ...el, status: EPromoStatus.ARCHIVED } : el
        )
      )
      message.success('Промокод отправлен в архив')
    } catch (e) {
      showServerError(e)
    }
  }

  const exportStatistics = async () => {
    try {
      setIsExportingStatistics(true)
      const response = await promoCodesApi.exportStatistics()
      const blob = response.data
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'promocodes_stats.xlsx' // можно оставить пустым — возьмёт с бэка
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsExportingStatistics(false)
    }
  }

  const columns: ColumnsType<IPromoCode> = useMemo(() => {
    const baseColumns: ColumnsType<IPromoCode> = [
      {
        title: 'Код',
        dataIndex: 'code',
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Input
                ref={codeSearchRef}
                value={codeSearch}
                onChange={(e) => setCodeSearch(e.target.value)}
                onPressEnter={() => {
                  saveFieldValue(codeSearch)
                  close()
                }}
                placeholder='ID Продавца'
                className='filterInput'
              />
              <div className='filterFooterButtons'>
                <Button
                  type='primary'
                  onClick={() => {
                    saveFieldValue(codeSearch)
                    close()
                  }}
                  icon={<SearchOutlined />}
                  size='small'
                  className='filterButton'
                >
                  Найти
                </Button>
                <Button
                  onClick={() => {
                    setCodeSearch('')
                    saveFieldValue('')
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            </div>
          )
        },
        onFilterDropdownOpenChange: (visible) => {
          if (visible) {
            setTimeout(() => codeSearchRef.current?.select(), 100)
          }
        },
        filterIcon: () => (
          <SearchOutlined
            style={{
              color: codeSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
            }}
          />
        )
      },
      {
        title: 'Скидка',
        dataIndex: 'discount',
        render: (
          _,
          { discount_value, discount_type, min_order_sum, tiers }
        ) => {
          const conditions = [
            { min_order_sum: min_order_sum ?? 0, discount_value },
            ...(tiers ?? [])
          ]
          return (
            <Space direction='vertical' size={0}>
              {conditions.map((condition) => (
                <Typography.Text key={condition.min_order_sum}>
                  {condition.discount_value}
                  {discount_type === EDiscountType.PERCENT ? '%' : '₽'}
                  {condition.min_order_sum > 0 && (
                    <Typography.Text type='secondary'>
                      {' '}
                      от {condition.min_order_sum} ₽
                    </Typography.Text>
                  )}
                </Typography.Text>
              ))}
            </Space>
          )
        }
      },
      {
        title: 'Срок действия',
        dataIndex: 'date',
        render: (_, { start_date, end_date }) => {
          return (
            <Typography.Text>
              {moment(start_date).format('DD.MM.YYYY')}
              {' - '}
              {moment(end_date).format('DD.MM.YYYY')}
            </Typography.Text>
          )
        }
      },
      {
        title: 'Применен',
        dataIndex: 'used_count'
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        render: (_, { status }) => {
          return (
            <Tag
              color={promoCodesStatuses[status].color}
              onClick={(event) => event.stopPropagation()}
            >
              {promoCodesStatuses[status].label}
            </Tag>
          )
        },
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Radio.Group
                onChange={(e) => {
                  setStatus(e.target.value)
                  onChangeStatus(e.target.value)
                  close()
                }}
                value={status}
                style={{ display: 'block', marginBottom: 15 }}
              >
                <Space direction='vertical' size={15}>
                  {Object.values(promoCodesStatuses).map((item) => (
                    <Radio key={item.value as string} value={item.value}>
                      <Tag color={item.color}>{item.label}</Tag>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
              <Button
                onClick={() => {
                  setStatus(undefined)
                  onChangeStatus(undefined)
                  close()
                }}
                size='small'
                className='filterButton'
              >
                Очистить
              </Button>
              <Button
                type='link'
                size='small'
                onClick={() => {
                  close()
                }}
              >
                Закрыть
              </Button>
            </div>
          )
        },
        filterIcon: () => (
          <FilterFilled
            style={{
              color: status ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
            }}
          />
        )
      },
      {
        title: '',
        dataIndex: 'archive',
        width: 60,
        render: (_, { _id, status }) =>
          status !== EPromoStatus.ARCHIVED && (
            <Popconfirm
              title='Отправить промокод в архив?'
              description='Промокод перестанет применяться.'
              okText='В архив'
              cancelText='Отмена'
              onConfirm={() => archivePromoCode(_id)}
            >
              {/* stopPropagation — клик по кнопке не должен открывать промокод */}
              <Button
                type='primary'
                danger
                icon={<InboxOutlined />}
                size='small'
                onClick={(event) => event.stopPropagation()}
              />
            </Popconfirm>
          )
      }
    ]

    if (isSuperAdmin && !sellerId) {
      const [codeColumn, discountColumn, dateColumn, ...otherColumns] =
        baseColumns
      const sellerColumn: ColumnsType<IPromoCode>[0] = {
        title: 'Продавец',
        dataIndex: 'sellers',
        width: 250,
        render: (_, { target_sellers }) => {
          if (!target_sellers.length) {
            return <Typography.Text>-</Typography.Text>
          }
          return (
            <Typography.Text>
              {target_sellers.slice(0, 3).map((seller, index) => (
                <Fragment key={seller._id}>
                  {index > 0 && ', '}
                  <CardLink to={getSellerEditPath(seller._id)}>
                    {seller.shop_details.shop_name}
                  </CardLink>
                </Fragment>
              ))}
              {target_sellers.length > 3 && '...'}
            </Typography.Text>
          )
        }
      }
      return [
        codeColumn,
        discountColumn,
        dateColumn,
        sellerColumn,
        ...otherColumns
      ]
    }

    return baseColumns
  }, [promoCodes, codeSearch, status, isSuperAdmin, sellerId])

  // Рендеры мобильных колонок берём из десктопных, чтобы правки не расходились
  const mobileColumns: ColumnsType<IPromoCode> = useMemo(() => {
    const findColumn = (dataIndex: string) =>
      columns.find((column) => (column as any).dataIndex === dataIndex)

    const codeColumn = findColumn('code')
    const discountColumn = findColumn('discount')
    const dateColumn = findColumn('date')
    const sellerColumn = findColumn('sellers')
    const statusColumn = findColumn('status')
    const archiveColumn = findColumn('archive')

    return [
      {
        ...codeColumn,
        title: (
          <MobileFilterTitle
            title='Промокод'
            columns={[codeColumn]}
            isActive={Boolean(codeSearch)}
          />
        ),
        width: '62%',
        filterDropdown: undefined,
        filterIcon: undefined,
        render: (value, record, index) => (
          <div className={styles.mobileCell}>
            <span className={styles.mobileCode}>{record.code}</span>
            <span className={styles.mobileSubText}>
              {discountColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
            <span className={styles.mobileSubText}>
              {dateColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
            {sellerColumn && (
              <span className={styles.mobileSubText}>
                {sellerColumn.render?.(value, record, index) as React.ReactNode}
              </span>
            )}
            <span className={styles.mobileSubText}>
              Применен: {record.used_count}
            </span>
          </div>
        )
      },
      {
        ...statusColumn,
        title: (
          <MobileFilterTitle
            title='Статус'
            columns={[statusColumn]}
            isActive={Boolean(status)}
          />
        ),
        width: '38%',
        filterDropdown: undefined,
        filterIcon: undefined,
        render: (value, record, index) => (
          <div className={styles.mobileStatusCell}>
            {statusColumn?.render?.(value, record, index) as React.ReactNode}
            {archiveColumn?.render?.(value, record, index) as React.ReactNode}
          </div>
        )
      }
    ]
  }, [columns, codeSearch, status])

  const isMaxActiveSellerPromoCodes = useMemo(() => {
    return Boolean(promoCodes.length >= 50 && (sellerId || userSellerId))
  }, [promoCodes, sellerId, userSellerId])

  // Архивные скрыты по умолчанию (как в баннерах); явный фильтр по статусу имеет приоритет
  const displayedPromoCodes = useMemo(() => {
    if (showArchived || status) return promoCodes
    return promoCodes.filter((el) => el.status !== EPromoStatus.ARCHIVED)
  }, [promoCodes, showArchived, status])

  return (
    <div>
      <div className={styles.header}>
        <MainTitle text='Промокоды' style={{ marginBottom: 0 }} hideOnMobile />
        <div className={styles.headerControls}>
          <Checkbox
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          >
            Показать архивные
          </Checkbox>
          {isSuperAdmin && !sellerId && (
            <Button
              type='default'
              size='large'
              icon={<LineChartOutlined />}
              loading={isExportingStatistics}
              onClick={exportStatistics}
            >
              Выгрузить статистику
            </Button>
          )}
          <Button
            type='primary'
            size='large'
            icon={<PlusOutlined />}
            onClick={() => {
              if (isSuperAdmin && !sellerId) {
                navigate(
                  superAdminRoutes.PROMOCODES_CREATE.replace('/:sellerId?', '')
                )
              } else if (isSuperAdmin && sellerId) {
                navigate(
                  superAdminRoutes.PROMOCODES_CREATE.replace(
                    ':sellerId',
                    sellerId
                  )
                )
              } else {
                navigate(adminRoutes.PROMOCODES_CREATE)
              }
            }}
            disabled={isMaxActiveSellerPromoCodes}
          >
            Создать промокод
          </Button>
        </div>
      </div>
      <Table
        scroll={isMobile ? undefined : { x: 950 }}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        columns={isMobile ? mobileColumns : columns}
        dataSource={displayedPromoCodes}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowClassName='tableRow'
        onRow={(record) => ({
          onClick: () => {
            if (isSuperAdmin && !sellerId) {
              navigate(
                superAdminRoutes.PROMOCODES_EDIT.replace(
                  ':id',
                  record._id
                ).replace('/:sellerId?', '')
              )
            } else if (isSuperAdmin && sellerId) {
              navigate(
                superAdminRoutes.PROMOCODES_EDIT.replace(
                  ':id',
                  record._id
                ).replace(':sellerId?', sellerId)
              )
            } else {
              navigate(adminRoutes.PROMOCODES_EDIT.replace(':id', record._id))
            }
          }
        })}
        footer={
          isMaxActiveSellerPromoCodes
            ? () => (
                <>
                  <InfoCircleOutlined style={{ color: '#FF4D4F' }} />
                  <Typography.Text style={{ color: '#FF4D4F', paddingLeft: 8 }}>
                    Внимание! Достигнуто максимальное число активных промокодов
                  </Typography.Text>
                </>
              )
            : undefined
        }
        pagination={{
          current: currentPage,
          onChange: onPageChange,
          pageSize,
          total,
          showSizeChanger: false
        }}
      />
    </div>
  )
}
